import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ClipboardCheck, Archive, AlertCircle } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { formatTimestamp } from '@/lib/date';
import { LandInspection } from '@property/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsTableMobile } from '@/hooks/use-is-table-mobile';
import { usePendingReviews, useReviewedInspections } from '@/hooks/use-inspection-queries';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';
import {
  DueDateCell,
  propertyAddressColumn,
  visitDateColumn,
} from '@/components/data-table/columns';

const PAGE_SIZE = 10;

const INSPECTION_STATUS = {
  pending_review: {
    label: 'Pending Review',
    className: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  },
  completed: {
    label: 'Approved',
    className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  },
  flagged: {
    label: 'Approved · Issues',
    className: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-500 border-red-500/20' },
  cancelled: { label: 'Cancelled', className: 'bg-muted text-muted-foreground border-border' },
} as const;

export function InspectionStatusBadge({ status }: { status: LandInspection['status'] }) {
  const { label, className } = INSPECTION_STATUS[status];
  return (
    <Badge
      variant="outline"
      className={cn('px-2.5 py-0.5 text-xs uppercase tracking-wider font-semibold', className)}
    >
      {label}
    </Badge>
  );
}

function OpenReviewsTable() {
  const navigate = useNavigate();
  const refresh = useTableRefresh(queryKeys.inspections.all());
  const isMobile = useIsTableMobile();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState<string | undefined>();

  const { data, total, isLoading, isFetching, isError } = usePendingReviews({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
    search,
  });

  const toDetail = (id: string) =>
    navigate({ to: '/dashboard/inspection/$inspectionId', params: { inspectionId: id } });

  const columns: ColumnDef<LandInspection>[] = [
    propertyAddressColumn<LandInspection>(),
    visitDateColumn<LandInspection>(isMobile),
    {
      accessorKey: 'reviewTaskDueDate',
      enableSorting: true,
      meta: { label: 'Due' },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Due" />,
      cell: ({ row }) => (
        <DueDateCell dueDate={row.original.reviewTaskDueDate} compact={isMobile} />
      ),
    },
    {
      id: 'action',
      enableSorting: false,
      meta: { label: 'Action', isMandatory: true },
      header: () => <span className="text-xs uppercase tracking-wider font-semibold">Action</span>,
      cell: ({ row }) => (
        <div>
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toDetail(row.original.inspectionId);
            }}
          >
            Review
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      tableId="reviewer-open-reviews"
      showColumnToggle
      onRefresh={refresh}
      columns={columns}
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
      sorting={{
        sortBy,
        sortOrder,
        onSortChange: (by, order) => {
          setSortBy(by);
          setSortOrder(order ?? 'asc');
        },
      }}
      onSearchChange={(q) => {
        setSearch(q || undefined);
        setPage(1);
      }}
      emptyState={
        isError
          ? {
              icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
              title: 'Failed to load inspections',
              description: 'There was a problem fetching pending reviews. Please try again.',
            }
          : {
              icon: <ClipboardCheck className="h-6 w-6 text-muted-foreground" />,
              title: 'No inspections pending review',
            }
      }
      getRowId={(row) => row.inspectionId}
      onRowClick={(row) => toDetail(row.inspectionId)}
    />
  );
}

function CompletedReviewsTable() {
  const navigate = useNavigate();
  const refresh = useTableRefresh(queryKeys.inspections.all());
  const isMobile = useIsTableMobile();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>('reviewedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState<string | undefined>();

  const { data, total, isLoading, isFetching, isError } = useReviewedInspections({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
    search,
  });

  const toDetail = (id: string) =>
    navigate({ to: '/dashboard/inspection/$inspectionId', params: { inspectionId: id } });

  const columns: ColumnDef<LandInspection>[] = [
    propertyAddressColumn<LandInspection>(),
    visitDateColumn<LandInspection>(isMobile),
    {
      accessorKey: 'status',
      enableSorting: false,
      meta: { label: 'Status' },
      header: () => <span className="text-xs uppercase tracking-wider font-semibold">Status</span>,
      cell: ({ row }) => <InspectionStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'reviewedAt',
      enableSorting: true,
      meta: { label: 'Reviewed', hideOnMobile: true },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Reviewed" className="pr-6" />
      ),
      cell: ({ row }) => (
        <span className="pr-6 text-sm text-muted-foreground whitespace-nowrap">
          {formatTimestamp(
            row.original.reviewedAt,
            isMobile ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' },
          )}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      tableId="reviewer-completed-reviews"
      showColumnToggle
      onRefresh={refresh}
      columns={columns}
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
      sorting={{
        sortBy,
        sortOrder,
        onSortChange: (by, order) => {
          setSortBy(by);
          setSortOrder(order ?? 'desc');
        },
      }}
      onSearchChange={(q) => {
        setSearch(q || undefined);
        setPage(1);
      }}
      emptyState={
        isError
          ? {
              icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
              title: 'Failed to load inspections',
              description: 'There was a problem fetching completed reviews. Please try again.',
            }
          : {
              icon: <Archive className="h-6 w-6 text-muted-foreground" />,
              title: 'No completed reviews yet',
            }
      }
      getRowId={(row) => row.inspectionId}
      onRowClick={(row) => toDetail(row.inspectionId)}
    />
  );
}

export function ReviewerInspections() {
  return (
    <div className="space-y-10">
      <section id="open-reviews">
        <div className="mb-4 flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-primary" />
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Open Reviews
          </h2>
        </div>
        <OpenReviewsTable />
      </section>

      <section id="completed-reviews">
        <div className="mb-4 flex items-center gap-2">
          <Archive className="h-6 w-6 text-muted-foreground" />
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Completed Reviews
          </h2>
        </div>
        <CompletedReviewsTable />
      </section>
    </div>
  );
}
